import { Fragment } from 'react';
import clsx from 'clsx';
import styles from '../../RelatedParties.module.scss';
import { alertIcon, alertIconDisabled } from '../../../../../../assets/hostedassets';
import { normalizeNameKey, toTitleCase } from '../../../../../../utils/utils';
import { FORMER_DIRECTOR_EDGE_TYPE, getImage, HOLDING_LABEL, HOLDING_PREFIX_EDGE_TYPES } from '../../relatedParty_constants';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { formatShareholdingPercent } from '../../../../../../utils/formatShareholdingPercent';
import DirectorHoverCardTooltip from '../DirectorHoverCard/DirectorHoverCardTooltip';
import type { DirectorHoverProfile } from '../DirectorHoverCard/DirectorHoverCard.types';
import EntityHoverCardTooltip from '../EntityHoverCard/EntityHoverCardTooltip';
import RowFinancialsDetails from '../EntityHoverCard/RowFinancialsDetails';
import {
    isNoDataCompanyRow,
    isPersonType,
    toNoDataEntityHoverProfile,
    toNoDataRowExtras,
    toRowFinancialsDetails,
} from '../EntityHoverCard/toEntityHoverProfile';
import { ENTITY_STATUS_PILL } from '../EntityHoverCard/EntityHoverCard.constants';
import type { HoverStatRow } from '../EntityHoverCard/EntityHoverCard.types';
import RptTransactionsCell from '../../RelatedPartyTransactions/RptTransactionsCell';
import EntityNameCell, { EntityNameLabel } from '../EntityNameCell/EntityNameCell';
import DisabledAlertsCell from '../DisabledAlertsCell/DisabledAlertsCell';
import type { RptParty } from '../../../../../../store/dashboard/relatedParties/relatedPartyTransactions.types';

function FirstDegreeRow({
    edgeType,
    isPDFReport,
    expandAll,
    entityEntry,
    handleAlertsClick,
    handleShowRelationsClick,
    isProbeEnabled,
    rptParty,
    onOpenRpt,
}: {
    edgeType: any;
    isPDFReport: any;
    expandAll: any;
    entityEntry: any;
    handleAlertsClick: any;
    handleShowRelationsClick: any;
    isProbeEnabled: any;
    rptParty?: RptParty;
    onOpenRpt?: (party: RptParty) => void;
}) {
    const shareholdingValue = entityEntry?.shareholdingPercentage;
    // polus stamps this server-side (injectDirectorProfileIntoRelatedParties, RECUR-27924) by
    // joining the related-party row to the management-profile roster by DIN/name — no separate
    // FE fetch or lookup needed. It is a SLIM projection — only what the hover card draws.
    const directorProfile: DirectorHoverProfile | undefined = entityEntry?.directorProfile;

    // Tooltip shows the RAW percentage (not the adaptive-precision one used in the row details)
    // so hovering reveals the exact underlying value regardless of display rounding.
    const rawPercentText = Number.isFinite(shareholdingValue) ? `${shareholdingValue}%` : '';
    const tooltipContent = [toTitleCase(entityEntry?.fromName) || '-', entityEntry?.designation, rawPercentText]
        .filter(Boolean)
        .join(' • ');
    const hasEntityHover = !isPDFReport && !!(entityEntry?.entityDetails || entityEntry?.aicaStatus);
    const rowFinancials = isPDFReport ? null : toRowFinancialsDetails(entityEntry?.aicaStatus, entityEntry?.type);
    const subtitleItems = isPDFReport ? [] : toNameSubtitleItems(edgeType, entityEntry, directorProfile?.isActive);
    const nameText = (
        <EntityNameLabel
            name={toTitleCase(entityEntry?.fromName) || '-'}
            iconSrc={isPDFReport ? undefined : getImage(entityEntry?.type)}
            subtitle={subtitleItems.length ? <NameSubtitle items={subtitleItems} /> : undefined}
        />
    );

    return (
        <tr
            key={entityEntry?.fromKid}
            className={clsx(styles.FirstDegreeRow, styles.FirstDegreeRow_Hover)}
            data-rp-director={normalizeNameKey(entityEntry?.fromName)}
            data-rp-din={entityEntry?.identifier ?? entityEntry?.din ?? ''}
            onClick={() => {
                handleShowRelationsClick(entityEntry, edgeType);
            }}
        >
            <td className={styles.RowItem}>
                <EntityNameCell
                    caret={
                        isPDFReport
                            ? undefined
                            : {
                                  disabled: !entityEntry?.rows?.length,
                                  expanded: !!(entityEntry?.rows?.length && (entityEntry.isExpanded || expandAll)),
                                  rotated: !!(entityEntry.isExpanded || (expandAll && entityEntry?.rows?.length)),
                              }
                    }
                >
                    {hasEntityHover ? (
                        <EntityHoverCardTooltip row={entityEntry}>{nameText}</EntityHoverCardTooltip>
                    ) : !isPDFReport && isNoDataCompanyRow(entityEntry) ? (
                        <EntityHoverCardTooltip
                            profile={toNoDataEntityHoverProfile(
                                toTitleCase(entityEntry.fromName),
                                entityEntry.fromName,
                                toNoDataRowExtras(entityEntry),
                            )}
                        >
                            {nameText}
                        </EntityHoverCardTooltip>
                    ) : (
                        <DirectorHoverCardTooltip
                            profile={isPDFReport ? undefined : directorProfile}
                            unmatched={
                                // Only people can have a management profile, so only they get the
                                // "no data" person card; a no-data company row gets the company card above.
                                !isPDFReport && !directorProfile && isPersonType(entityEntry?.type)
                                    ? {
                                          name: entityEntry?.fromName,
                                          designation: entityEntry?.designation,
                                          shareholdingPercentage: shareholdingValue,
                                          din: entityEntry?.fromKid,
                                          isFormer: edgeType === FORMER_DIRECTOR_EDGE_TYPE,
                                      }
                                    : undefined
                            }
                            fallback={tooltipContent}
                        >
                            {nameText}
                        </DirectorHoverCardTooltip>
                    )}
                </EntityNameCell>
            </td>

            <td className={styles.RowItem}>{/* {getRelationType(entityEntry?.edgeType, entityEntry?.type) || '-'} */}</td>

            <td className={styles.RowItem}>
                {/* <div className={styles.EntityNamePill}>
                    {toTitleCase(entityEntry?.toName) || '-'}
                </div> */}
            </td>

            <td className={styles.RowItem}>
                {isPDFReport ? (
                    <span className={styles.DetailsText}>{toPdfDetailsText(edgeType, entityEntry)}</span>
                ) : (
                    rowFinancials && <RowFinancialsDetails details={rowFinancials} row={entityEntry} />
                )}
            </td>

            <td className={styles.RowItem}>
                <div
                    className={clsx(styles.Risk, {
                        [styles.Risk_Severe]: entityEntry?.risk === 'SEVERE',
                        [styles.Risk_High]: entityEntry?.risk === 'HIGH',
                        [styles.Risk_Medium]: entityEntry?.risk === 'MEDIUM',
                        [styles.Risk_Low]: entityEntry?.risk === 'LOW',
                    })}
                >
                    {entityEntry?.risk && entityEntry?.risk !== 'null' ? toTitleCase(entityEntry?.risk) : 'NA'}
                </div>
            </td>

            {!isPDFReport && <td className={styles.RowItem}>{onOpenRpt && <RptTransactionsCell party={rptParty} onOpen={onOpenRpt} />}</td>}

            {!isPDFReport && (
                <td className={styles.RowItem}>
                    {isProbeEnabled && !entityEntry?.alerts?.length ? (
                        <Tooltip
                            content="AICA can't fetch alerts for this"
                            direction="top"
                            contentCss={{ color: 'black', whiteSpace: 'nowrap' }}
                            fitContent
                            small
                            usePortal
                        >
                            <DisabledAlertsCell />
                        </Tooltip>
                    ) : (
                        <div
                            className={clsx(styles.Alerts, {
                                [styles.Alerts_Disabled]: !entityEntry?.alerts?.length,
                            })}
                            onClick={() => {
                                handleAlertsClick(entityEntry);
                            }}
                        >
                            <img
                                src={entityEntry?.alerts?.length ? alertIcon : alertIconDisabled}
                                alt="alert"
                                style={{
                                    margin: '0px 5px 2px 0',
                                }}
                            />
                            <span>{entityEntry?.alerts?.length} Alerts</span>
                        </div>
                    )}
                </td>
            )}
        </tr>
    );
}

type NameSubtitleItem = { text: string; tone?: HoverStatRow['tone'] };

/** Designation (or "Holding" for holding edges), shareholding % and director Active/Inactive, in that order. */
function toNameSubtitleItems(edgeType: EdgeType, entityEntry: EntityEntry, isActive?: boolean | null): NameSubtitleItem[] {
    const percent = formatShareholdingPercent(entityEntry?.shareholdingPercentage, { hideWhenNull: true });
    const lead = HOLDING_PREFIX_EDGE_TYPES.has(edgeType) ? percent && HOLDING_LABEL : entityEntry?.designation;
    const items: NameSubtitleItem[] = [lead, percent].filter((text): text is string => Boolean(text)).map((text) => ({ text }));
    if (isActive === true) items.push({ text: ENTITY_STATUS_PILL.ACTIVE, tone: 'positive' });
    if (isActive === false) items.push({ text: ENTITY_STATUS_PILL.INACTIVE, tone: 'negative' });
    return items;
}

function NameSubtitle({ items }: { items: NameSubtitleItem[] }) {
    return (
        <>
            {items.map((item, index) => (
                <Fragment key={item.text}>
                    {index > 0 && <span className={styles.SubtitleDot} aria-hidden="true" />}
                    <span
                        className={clsx(styles.SubtitleItem, {
                            [styles.SubtitleItem_Positive]: item.tone === 'positive',
                            [styles.SubtitleItem_Negative]: item.tone === 'negative',
                        })}
                    >
                        {item.text}
                    </span>
                </Fragment>
            ))}
        </>
    );
}

/** PDF details column: "Holding: 80.00%" for holding edges, else "Director • 50.00%". */
function toPdfDetailsText(edgeType: EdgeType, entityEntry: EntityEntry): string {
    const separator = HOLDING_PREFIX_EDGE_TYPES.has(edgeType) ? ': ' : ' • ';
    return toNameSubtitleItems(edgeType, entityEntry)
        .map((item) => item.text)
        .join(separator);
}

export default FirstDegreeRow;
